/**
 * Bible Mysteries Decoder — overlay direction concepts, for Yash to pick from.
 * Background = real style-test plate (kadosh_closeup_3_cinematic_filmstill), so
 * these render on an actual 16:9 frame, not a mockup. Three directions:
 *   A — Evidence Board (case-file, cool steel-blue, monospace citation tags)
 *   B — Manuscript Margin (parchment/ink, serif, wax-seal citation)
 *   C — Forensic Lower-Third (minimal BBC/NatGeo, sans-serif, decode animation)
 * NOT wired into any production — pure comparison preview.
 */
import { AbsoluteFill, Img, useCurrentFrame, useVideoConfig, interpolate, Easing } from "remotion";
import { loadFont as loadMono } from "@remotion/google-fonts/IBMPlexMono";
import { loadFont as loadSans } from "@remotion/google-fonts/Inter";
import { loadFont as loadSerif } from "@remotion/google-fonts/EBGaramond";
import { loadFont as loadStamp } from "@remotion/google-fonts/SpecialElite";

const { fontFamily: mono } = loadMono("normal", { weights: ["400", "500", "600"], subsets: ["latin"] });
const { fontFamily: sans } = loadSans("normal", { weights: ["400", "500", "600", "700", "800"], subsets: ["latin"] });
const { fontFamily: serif } = loadSerif("normal", { weights: ["400", "500", "600"], subsets: ["latin"] });
const { fontFamily: stamp } = loadStamp("normal", { weights: ["400"], subsets: ["latin"] });

const BG_URL = "https://pub-bb3ce5df1bc243da96f744a22c134897.r2.dev/beyond_bible/1a_bible_decoder/_style_tests/closeup/kadosh_closeup_3_cinematic_filmstill.jpg";

const Plate: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <AbsoluteFill style={{ background: "#000" }}>
    <Img src={BG_URL} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
    {children}
  </AbsoluteFill>
);

const useFadeIn = (delay: number, dur = 18) => {
  const frame = useCurrentFrame();
  return interpolate(frame, [delay, delay + dur], [0, 1], {
    extrapolateLeft: "clamp", extrapolateRight: "clamp", easing: Easing.out(Easing.cubic),
  });
};

// ═══════════════════════════════════════════════════════════════════════
// CONCEPT A — EVIDENCE BOARD (case-file / forensic)
// ═══════════════════════════════════════════════════════════════════════
export const ConceptA_EvidenceBoard: React.FC = () => {
  const frame = useCurrentFrame();
  const inA = useFadeIn(6);
  const inB = useFadeIn(24);
  const redactW = interpolate(frame, [0, 20], [0, 100], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });

  return (
    <Plate>
      {/* top-left redaction-bar reveal */}
      <div style={{
        position: "absolute", top: 56, left: 56,
        background: "rgba(10,14,20,0.88)", border: "1px solid rgba(120,170,210,0.35)",
        padding: "10px 18px", opacity: inA,
      }}>
        <div style={{ fontFamily: mono, fontSize: 13, letterSpacing: 2, color: "#8fb4d6", textTransform: "uppercase" }}>
          Exhibit 05 · Exodus 6:3
        </div>
      </div>

      {/* split translation comparison, lower third */}
      <div style={{
        position: "absolute", left: 56, right: 56, bottom: 64,
        background: "linear-gradient(180deg, rgba(8,12,18,0.15) 0%, rgba(8,12,18,0.90) 30%, rgba(8,12,18,0.94) 100%)",
        padding: "28px 32px 24px", opacity: inB,
      }}>
        <div style={{ display: "flex", gap: 40, marginBottom: 14 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontFamily: mono, fontSize: 11, letterSpacing: 3, color: "#6b8299", marginBottom: 4 }}>YOUR BIBLE SAYS</div>
            <div style={{ fontFamily: sans, fontSize: 26, fontWeight: 700, color: "#e8edf2" }}>El Shaddai</div>
          </div>
          <div style={{ width: 1, background: "rgba(143,180,214,0.35)" }} />
          <div style={{ flex: 1 }}>
            <div style={{ fontFamily: mono, fontSize: 11, letterSpacing: 3, color: "#6b8299", marginBottom: 4 }}>GOD'S OWN WORDS SAY</div>
            <div style={{ fontFamily: sans, fontSize: 26, fontWeight: 700, color: "#8fd6c4" }}>Not by my name YHWH</div>
          </div>
        </div>
        {/* redaction bar */}
        <div style={{
          height: 22, width: `${redactW}%`, background: "#161c24",
          border: "1px solid rgba(120,170,210,0.3)", position: "relative", overflow: "hidden",
        }}>
          <div style={{
            position: "absolute", inset: 0, fontFamily: mono, fontSize: 12, color: "#3a4652",
            display: "flex", alignItems: "center", paddingLeft: 8, letterSpacing: 1,
          }}>
            ▓▓▓▓▓▓▓ CASE FILE #05 ▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓
          </div>
        </div>
      </div>

      {/* corner case-number stamp */}
      <div style={{
        position: "absolute", top: 56, right: 56, opacity: inA,
        fontFamily: mono, fontSize: 12, color: "#5d7488", letterSpacing: 2,
        border: "1px solid rgba(120,170,210,0.3)", padding: "6px 12px",
      }}>
        BIBLE MYSTERIES DECODER · W05
      </div>
    </Plate>
  );
};

// ═══════════════════════════════════════════════════════════════════════
// CONCEPT B — MANUSCRIPT MARGIN (parchment / scholarly annotation)
// ═══════════════════════════════════════════════════════════════════════
export const ConceptB_ManuscriptMargin: React.FC = () => {
  const inA = useFadeIn(6);
  const inB = useFadeIn(28);

  return (
    <Plate>
      {/* margin note, right side */}
      <div style={{
        position: "absolute", top: 90, right: 60, width: 340,
        background: "linear-gradient(135deg, rgba(233,220,195,0.94), rgba(214,198,166,0.94))",
        borderLeft: "3px solid #6b5637", padding: "18px 22px", opacity: inA,
        boxShadow: "0 8px 28px rgba(0,0,0,0.45)",
        transform: "rotate(-1deg)",
      }}>
        <div style={{ fontFamily: serif, fontSize: 20, fontStyle: "italic", color: "#3a2c1a", lineHeight: 1.35 }}>
          "El Shaddai — the fathers' word. Borrowed from the same root as Ugarit's own god."
        </div>
      </div>

      {/* wax-seal style citation, lower center */}
      <div style={{
        position: "absolute", bottom: 80, left: "50%", transform: "translateX(-50%)",
        textAlign: "center", opacity: inB,
      }}>
        <div style={{
          fontFamily: stamp, fontSize: 22, color: "#e9dcc3", letterSpacing: 1,
          background: "rgba(58,44,26,0.82)", padding: "10px 26px", border: "1px solid rgba(233,220,195,0.4)",
        }}>
          ✦ Exodus 6:3 ✦
        </div>
      </div>

      {/* small ink corner flourish top-left */}
      <div style={{
        position: "absolute", top: 56, left: 56, opacity: inA,
        fontFamily: serif, fontSize: 15, color: "#e9dcc3", fontStyle: "italic",
        borderBottom: "1px solid rgba(233,220,195,0.4)", paddingBottom: 4,
      }}>
        Bible Mysteries Decoder — Marginalia No. 5
      </div>
    </Plate>
  );
};

// ═══════════════════════════════════════════════════════════════════════
// CONCEPT C — FORENSIC LOWER-THIRD (minimal, BBC/NatGeo)
// ═══════════════════════════════════════════════════════════════════════
export const ConceptC_ForensicLowerThird: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const inA = useFadeIn(4, 12);
  const slideX = interpolate(frame, [4, 22], [-16, 0], { extrapolateLeft: "clamp", extrapolateRight: "clamp", easing: Easing.out(Easing.cubic) });

  // simple letter-decode: word resolves from random glyphs to the real word
  const word = "EL SHADDAI";
  const decodeStart = 20, decodeDur = 26;
  const revealCount = Math.floor(interpolate(frame, [decodeStart, decodeStart + decodeDur], [0, word.length], { extrapolateLeft: "clamp", extrapolateRight: "clamp" }));
  const glyphs = "XQZKVWY#%&";
  const displayed = word.split("").map((ch, i) => (i < revealCount || ch === " " ? ch : glyphs[(frame + i * 7) % glyphs.length])).join("");

  return (
    <Plate>
      <div style={{
        position: "absolute", left: 60, bottom: 72,
        opacity: inA, transform: `translateX(${slideX}px)`,
      }}>
        <div style={{ fontFamily: mono, fontSize: 30, fontWeight: 500, color: "#f2f4f6", letterSpacing: 3 }}>
          {displayed}
        </div>
        <div style={{ width: 64, height: 2, background: "#9aa7b2", margin: "10px 0" }} />
        <div style={{ fontFamily: sans, fontSize: 15, color: "#c3cbd2", fontWeight: 400 }}>
          Exodus 6:3 · the name the fathers knew
        </div>
      </div>

      <div style={{
        position: "absolute", top: 52, right: 60, opacity: inA,
        fontFamily: sans, fontSize: 12, color: "#8b949c", letterSpacing: 2, textTransform: "uppercase",
      }}>
        Bible Mysteries Decoder
      </div>
    </Plate>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
